import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../auth/AuthContext";
import { ImageField } from "../../../components/admin/ImageField";
import { formatPrice, formatMonthlyKm } from "@/lib/price";

const EMPTY = {
  name: "", slug: "", brand: "", model: "", segment: "", badge: "",
  price_label: "", price_amount: "", vat_rate: 20, year: 2026, km: 0,
  color: "", engine: "", transmission: "", fuel: "", seats: 5,
  status: "müsait", description: "", featuresText: "", image_url: "",
  gallery: [], sort_order: 0, monthly_km: "",
};

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-3 py-2.5 text-sm text-[#404040] focus:outline-none focus:border-[#B09060]";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-[#8F7648] mb-1";

// "müsait" ve "kirada" kiralama sistemi tarafından otomatik kullanılır, silinemez.
const FIXED_STATUSES = ["müsait", "kirada"];
const STATUS_STYLE = {
  "müsait": "bg-emerald-50 text-emerald-700",
  "kirada": "bg-blue-50 text-blue-700",
  "bakımda": "bg-amber-50 text-amber-700",
};
const statusStyle = (s) => STATUS_STYLE[s] || "bg-[#F2F3EB] text-[#6B6B6B]";
const upper = (s) => (s || "").toLocaleUpperCase("tr-TR");

function parseStatuses(value) {
  try {
    const arr = JSON.parse(value || "[]");
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string" && x.trim()) : [];
  } catch {
    return [];
  }
}

function slugify(s) {
  return s.toLowerCase()
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function VehiclesSection() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customStatuses, setCustomStatuses] = useState(["bakımda"]);
  const [statusEditorOpen, setStatusEditorOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const allStatuses = [...FIXED_STATUSES, ...customStatuses];

  useEffect(() => {
    supabase
      .from("site_settings")
      .select("value")
      .eq("key", "vehicle_statuses")
      .maybeSingle()
      .then(({ data }) => {
        if (data) setCustomStatuses(parseStatuses(data.value));
      });
  }, []);

  async function persistStatuses(next) {
    const { error } = await supabase
      .from("site_settings")
      .upsert({ key: "vehicle_statuses", value: JSON.stringify(next) });
    if (error) {
      toast.error(`Durumlar kaydedilemedi: ${error.message}`);
      return false;
    }
    setCustomStatuses(next);
    return true;
  }

  async function addStatus() {
    const name = newStatus.trim().toLocaleLowerCase("tr-TR");
    if (!name) return;
    if (allStatuses.includes(name)) {
      toast.error("Bu durum zaten var.");
      return;
    }
    if (await persistStatuses([...customStatuses, name])) {
      setNewStatus("");
      toast.success(`"${upper(name)}" durumu eklendi.`);
    }
  }

  async function removeStatus(name) {
    const inUse = vehicles.filter((v) => v.status === name).length;
    const msg = inUse
      ? `"${upper(name)}" durumu ${inUse} araçta kullanılıyor. Silinirse bu araçlar seçimde yine görünür ama yeni araçlara atanamaz. Silinsin mi?`
      : `"${upper(name)}" durumu silinsin mi?`;
    if (!window.confirm(msg)) return;
    if (await persistStatuses(customStatuses.filter((x) => x !== name))) toast.success("Durum silindi.");
  }

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("vehicles")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) toast.error(`Araçlar yüklenemedi: ${error.message}`);
    else setVehicles(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const row = {
      name: editing.name,
      slug: editing.slug || slugify(editing.name),
      brand: editing.brand || null,
      model: editing.model || null,
      segment: editing.segment || null,
      badge: editing.badge || null,
      price_label: editing.price_label || null,
      price_amount: editing.price_amount === "" ? null : Number(editing.price_amount),
      vat_rate: Number(editing.vat_rate) || 20,
      year: Number(editing.year) || null,
      km: Number(editing.km) || 0,
      color: editing.color || null,
      engine: editing.engine || null,
      transmission: editing.transmission || null,
      fuel: editing.fuel || null,
      seats: Number(editing.seats) || null,
      status: editing.status,
      description: editing.description || null,
      features: editing.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      image_url: editing.image_url || null,
      gallery: (editing.gallery || []).filter(Boolean),
      sort_order: Number(editing.sort_order) || 0,
      monthly_km: editing.monthly_km === "" || editing.monthly_km === null ? null : Number(editing.monthly_km) || null,
    };
    const { error } = editing.id
      ? await supabase.from("vehicles").update(row).eq("id", editing.id)
      : await supabase.from("vehicles").insert(row);
    setSaving(false);
    if (error) {
      toast.error(`Kaydedilemedi: ${error.message}. (Yeni alanlar için migration gerekli olabilir)`);
    } else {
      toast.success(editing.id ? "Araç güncellendi — siteye otomatik yansıdı." : "Araç eklendi — siteye otomatik yansıdı.");
      setEditing(null);
      load();
    }
  }

  async function remove(v) {
    if (!window.confirm(`"${v.name}" silinsin mi? (Aktif kiralaması varsa veritabanı engeller)`)) return;
    const { error } = await supabase.from("vehicles").delete().eq("id", v.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Araç silindi.");
      setVehicles((vs) => vs.filter((x) => x.id !== v.id));
    }
  }

  if (loading) return <div className="text-[#6B6B6B] text-sm py-10" data-testid="vehicles-loading">Yükleniyor...</div>;

  return (
    <div data-testid="vehicles-section">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-[#404040]">Araç Yönetimi</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">{vehicles.length} araç · değişiklikler siteye otomatik yansır</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="vehicle-add-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#B09060] text-white text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Araç
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mt-6">
        {vehicles.map((v) => (
          <div key={v.id} data-testid={`vehicle-row-${v.slug}`} className="bg-white border border-[#B09060]/20 rounded-sm overflow-hidden shadow-[0_4px_20px_rgba(176,144,96,0.05)]">
            {v.image_url && <img src={v.image_url} alt={v.name} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-[#404040] text-sm">{v.name}</div>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusStyle(v.status || "müsait")}`} data-testid={`vehicle-status-${v.slug}`}>
                  {v.status || "müsait"}
                </span>
              </div>
              <div className="text-xs text-[#6B6B6B] mt-1">
                {v.segment} · {v.year}{v.km ? ` · ${v.km} km` : ""}
              </div>
              <div className="text-[#8F7648] font-semibold text-sm mt-1.5">
                {formatPrice(v.price_label)} {v.monthly_km ? <span className="text-[#6B6B6B] font-normal text-[11px]">· {formatMonthlyKm(v.monthly_km)}</span> : null} <span className="text-[#9B9B8F] font-normal text-[11px]">+ %{v.vat_rate ?? 20} KDV/Ay</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setEditing({ ...EMPTY, ...v, featuresText: (v.features || []).join("\n"), gallery: v.gallery || [], monthly_km: v.monthly_km ?? "" })}
                  data-testid={`vehicle-edit-${v.slug}`}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-sm border border-[#B09060]/30 text-[#8F7648] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" /> Düzenle
                </button>
                <button
                  onClick={() => remove(v)}
                  data-testid={`vehicle-delete-${v.slug}`}
                  className="p-2 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="vehicle-edit-dialog">
          <form onSubmit={save} className="w-full max-w-3xl bg-white border border-[#B09060]/20 rounded-sm p-7 space-y-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl text-[#404040]">{editing.id ? "Aracı Düzenle" : "Yeni Araç"}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="vehicle-dialog-close" className="text-[#9B9B8F] hover:text-[#404040]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Araç Adı *</label>
                <input required data-testid="vehicle-form-name" className={inputCls} value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })} />
              </div>
              <div>
                <label className={labelCls}>Durum</label>
                <select data-testid="vehicle-form-status" className={inputCls} value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {[...allStatuses, ...(editing.status && !allStatuses.includes(editing.status) ? [editing.status] : [])].map((s) => (
                    <option key={s} value={s}>{upper(s)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setStatusEditorOpen((o) => !o)}
                  data-testid="vehicle-status-editor-toggle"
                  className="mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#8F7648] hover:text-[#B09060]"
                >
                  {statusEditorOpen ? "Kapat" : "Durumları düzenle"}
                </button>
                {statusEditorOpen && (
                  <div className="mt-2 p-3 border border-[#B09060]/25 rounded-sm bg-[#FAFBF6] space-y-2" data-testid="vehicle-status-editor">
                    <div className="flex flex-wrap gap-1.5">
                      {allStatuses.map((st) => (
                        <span key={st} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider ${statusStyle(st)}`}>
                          {upper(st)}
                          {!FIXED_STATUSES.includes(st) && (
                            <button type="button" aria-label={`${st} durumunu sil`} onClick={() => removeStatus(st)} className="hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-1.5">
                      <input
                        className={`${inputCls} py-1.5 text-xs uppercase`}
                        placeholder="Yeni durum"
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStatus(); } }}
                        data-testid="vehicle-status-new-input"
                      />
                      <button type="button" onClick={addStatus} data-testid="vehicle-status-add"
                        className="px-3 rounded-sm bg-[#B09060] text-white hover:bg-[#8F7648] shrink-0" aria-label="Durum ekle">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-[10px] text-[#9B9B8F]">MÜSAİT ve KİRADA kiralama sistemi tarafından kullanıldığı için silinemez.</p>
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>Marka</label>
                <input className={inputCls} value={editing.brand || ""} placeholder="Mercedes-Benz"
                  onChange={(e) => setEditing({ ...editing, brand: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Model</label>
                <input className={inputCls} value={editing.model || ""} placeholder="E180 Sedan"
                  onChange={(e) => setEditing({ ...editing, model: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>URL (slug)</label>
                <input className={inputCls} value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Segment</label>
                <input className={inputCls} value={editing.segment || ""} placeholder="Prestige Executive"
                  onChange={(e) => setEditing({ ...editing, segment: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Rozet</label>
                <input className={inputCls} value={editing.badge || ""} placeholder="En Çok Tercih Edilen"
                  onChange={(e) => setEditing({ ...editing, badge: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Model Yılı</label>
                <input type="number" className={inputCls} value={editing.year || ""}
                  onChange={(e) => setEditing({ ...editing, year: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Kilometre</label>
                <input type="number" data-testid="vehicle-form-km" className={inputCls} value={editing.km ?? ""}
                  onChange={(e) => setEditing({ ...editing, km: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Aylık Kullanım KM</label>
                <input type="number" min="0" step="500" data-testid="vehicle-form-monthly-km" className={inputCls}
                  value={editing.monthly_km ?? ""} placeholder="örn. 3000"
                  onChange={(e) => setEditing({ ...editing, monthly_km: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Aylık Fiyat (etiket) *</label>
                <input required data-testid="vehicle-form-price" className={inputCls} value={editing.price_label || ""} placeholder="200.000 (₺ işareti otomatik eklenir)"
                  onChange={(e) => setEditing({ ...editing, price_label: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Aylık Fiyat (sayısal, TL)</label>
                <input type="number" className={inputCls} value={editing.price_amount ?? ""} placeholder="200000"
                  onChange={(e) => setEditing({ ...editing, price_amount: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>KDV Oranı (%)</label>
                <input type="number" className={inputCls} value={editing.vat_rate ?? 20}
                  onChange={(e) => setEditing({ ...editing, vat_rate: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Renk</label>
                <input className={inputCls} value={editing.color || ""}
                  onChange={(e) => setEditing({ ...editing, color: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Motor</label>
                <input className={inputCls} value={editing.engine || ""}
                  onChange={(e) => setEditing({ ...editing, engine: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Şanzıman</label>
                <input className={inputCls} value={editing.transmission || ""}
                  onChange={(e) => setEditing({ ...editing, transmission: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Yakıt</label>
                <input className={inputCls} value={editing.fuel || ""}
                  onChange={(e) => setEditing({ ...editing, fuel: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Koltuk</label>
                <input type="number" className={inputCls} value={editing.seats || ""}
                  onChange={(e) => setEditing({ ...editing, seats: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Sıralama</label>
                <input type="number" className={inputCls} value={editing.sort_order}
                  onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </div>
              <div className="sm:col-span-3">
                <label className={labelCls}>Açıklama</label>
                <textarea rows={3} className={inputCls} value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="sm:col-span-3">
                <label className={labelCls}>Donanımlar (her satıra bir tane)</label>
                <textarea rows={4} className={inputCls} value={editing.featuresText}
                  onChange={(e) => setEditing({ ...editing, featuresText: e.target.value })} />
              </div>
              <div className="sm:col-span-3">
                <label className={labelCls}>Kapak Görseli</label>
                <ImageField
                  value={editing.image_url}
                  onChange={(url) => setEditing({ ...editing, image_url: url })}
                  userId={user?.id}
                  testid="vehicle-form-image"
                />
              </div>
              <div className="sm:col-span-3">
                <label className={labelCls}>Ek Fotoğraflar (galeri — istediğiniz kadar ekleyin)</label>
                <div className="space-y-3">
                  {(editing.gallery || []).map((url, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        className={inputCls}
                        value={url}
                        data-testid={`vehicle-gallery-input-${i}`}
                        onChange={(e) =>
                          setEditing({ ...editing, gallery: editing.gallery.map((g, gi) => (gi === i ? e.target.value : g)) })
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, gallery: editing.gallery.filter((_, gi) => gi !== i) })}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-sm"
                        data-testid={`vehicle-gallery-remove-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, gallery: [...(editing.gallery || []), ""] })}
                    data-testid="vehicle-gallery-add"
                    className="text-xs font-semibold uppercase tracking-wider text-[#8F7648] border border-[#B09060]/30 px-3.5 py-2 rounded-sm hover:bg-[#F2F3EB] transition-colors"
                  >
                    + Fotoğraf Alanı Ekle
                  </button>
                  <p className="text-[11px] text-[#9B9B8F]">İpucu: Kapak görselini "Yükle" ile yükleyip URL'yi buraya da kopyalayabilirsiniz.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                data-testid="vehicle-form-save"
                className="flex-1 py-3 rounded-sm bg-[#B09060] text-white font-semibold text-xs uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors disabled:opacity-50"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-6 py-3 rounded-sm border border-[#B09060]/30 text-[#404040] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
              >
                Vazgeç
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
